import _ from "lodash";
/** external */
import React, { PureComponent } from "react";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import classNames from "classnames";
import {
  TickLargeOutlined,
  CloseCircleFilled,
  TickCircleFilled,
  CircleOutlined,
} from "@toddle-edu/ds-icons";
import { RadioButton } from "@toddle-edu/ds-web";
/** internal */
import { generateRandomId } from "Utils";
/** relative */
import classes from "./RadioButtonPicker.scss";

class RadioButtonPicker extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selectedValue: props.value,
    };

    if (props.customRef) {
      props.customRef(this);
    }
  }

  componentDidUpdate(prevProps) {
    const { mode, value } = this.props;
    if (!_.isEqual(prevProps?.mode, mode)) {
      this.setState({ selectedValue: value });
    }
  }

  handleOnChange = optionValue => {
    const { updateInputField } = this.props;

    updateInputField({ params: { radio: optionValue } });
    this.setState({ selectedValue: optionValue });
  };

  isValid = () => {
    return 0;
  };

  renderView = () => {
    const {
      options,
      mode,
      width,
      correctAnswer,
      isAiGeneratedField,
    } = this.props;
    const { selectedValue } = this.state;
    const randomId = generateRandomId();

    const isInactiveQuestionRevamp = _.includes(
      ["preview", "restricted-view"],
      mode
    );

    const containerClasses = classNames({
      [classes.container]: !isInactiveQuestionRevamp,
      [classes.containerV2]: isInactiveQuestionRevamp,
    });
    return (
      <div className={containerClasses}>
        {_.map(options, ({ label, value: optionValue }) => {
          let isChecked = false;

          if (isAiGeneratedField) {
            isChecked = optionValue?.correct == correctAnswer?.correct;
          } else if (mode == "preview") {
            isChecked = selectedValue?.correct === optionValue?.correct;
          } else if (mode !== "restricted-view") {
            isChecked =
              selectedValue?.studentResponse === optionValue?.studentResponse;
          }

          const buttonStyles = { ...(width && { width }) };
          const buttonContainer = classNames({
            [classes.button]: !isInactiveQuestionRevamp,
            [classes.selected]: isChecked,
            [classes.buttonV2]: isInactiveQuestionRevamp,
          });
          const labelStyle = classNames({
            [classes.label]: true,
          });
          const labelStylePreview = classNames({
            [classes.labelPreview]:
              mode == "preview" && isChecked && !isInactiveQuestionRevamp,
            [classes.label]: !isInactiveQuestionRevamp,
            [classes.labelPreviewV2]: isInactiveQuestionRevamp,
            "text-body-l": isInactiveQuestionRevamp,
          });

          const circleOutlinedClasses = classNames(
            classes.iconDisabled,
            classes.flexShrink0
          );

          return (
            <div key={optionValue?.correct}>
              <div className={buttonContainer} style={buttonStyles}>
                {isInactiveQuestionRevamp ? (
                  <span className={labelStylePreview}>
                    {isInactiveQuestionRevamp &&
                      (isChecked ? (
                        <TickCircleFilled
                          size="x-small"
                          variant="success"
                          className={classes.flexShrink0}
                        />
                      ) : (
                        <CircleOutlined
                          size="x-small"
                          className={circleOutlinedClasses}
                        />
                      ))}
                    {label}{" "}
                    {isChecked && !isInactiveQuestionRevamp && (
                      <TickLargeOutlined variant="success" size="xx-small" />
                    )}
                  </span>
                ) : (
                  <RadioButton
                    checked={isChecked}
                    name={randomId}
                    testId={`worksheet-radioButtonPicker-view-${_.camelCase(
                      label
                    )}`}
                  >
                    <span className={labelStyle}>{label}</span>
                  </RadioButton>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderEvaluationView = () => {
    const { options, correctAnswer, value } = this.props;
    const randomId = generateRandomId();

    return (
      <div className={classes.container}>
        {_.map(options, ({ label, value: optionValue }) => {
          const isStudentCorrect =
            value?.studentResponse === correctAnswer?.correct;
          const isCorrect =
            correctAnswer?.correct === optionValue?.studentResponse;

          const buttonContainer = classNames({
            [classes.button]: true,
            [classes.selected]: isCorrect && isStudentCorrect,
            [classes.incorrect]:
              value?.studentResponse === optionValue?.studentResponse &&
              !isStudentCorrect,
          });
          const labelStyle = classNames({
            [classes.evaluateLabel]: true,
          });

          return (
            <div key={optionValue?.correct}>
              <div className={buttonContainer}>
                {value?.studentResponse === optionValue?.studentResponse ? (
                  <div className={classes.selectedOption}>
                    {isCorrect ? (
                      <TickCircleFilled variant="success" />
                    ) : (
                      <CloseCircleFilled variant="critical" />
                    )}
                    <span className={labelStyle}>{label}</span>
                  </div>
                ) : (
                  <RadioButton
                    checked={
                      value?.studentResponse === optionValue?.studentResponse
                    }
                    name={randomId}
                    testId={`worksheet-radioButtonPicker-evaluationView-${_.camelCase(
                      label
                    )}`}
                  >
                    <span className={labelStyle}>{label}</span>
                  </RadioButton>
                )}
                {isCorrect ? (
                  <TickLargeOutlined variant="link" size="xx-small" />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderEdit = () => {
    const { options, mode } = this.props;
    const { selectedValue } = this.state;
    const randomId = generateRandomId();

    return (
      <div>
        <div className={classes.container}>
          {_.map(options, ({ label, value: optionValue }) => {
            const isChecked =
              mode == "create"
                ? selectedValue?.correct === optionValue?.correct
                : selectedValue?.studentResponse ===
                  optionValue?.studentResponse;
            const buttonContainer = classNames({
              [classes.button]: true,
              [classes.selected]: isChecked,
            });
            const labelStyle = classNames({
              [classes.label]: true,
              [classes.selectedText]: isChecked,
            });
            return (
              <div
                className={buttonContainer}
                key={optionValue?.correct}
                onClick={() => this.handleOnChange(optionValue)}
                onKeyDown={e =>
                  onEnterOrSpaceKeyDown(e, () =>
                    this.handleOnChange(optionValue)
                  )
                }
                role="button"
                tabIndex={0}
                aria-label={label}
                data-test-id={`worksheet-radioButtonPicker-option-${_.camelCase(
                  label
                )}`}
              >
                <RadioButton
                  checked={isChecked}
                  name={randomId}
                  testId={`worksheet-radioButtonPicker-editView-${_.camelCase(
                    label
                  )}`}
                >
                  <span className={labelStyle}>{label}</span>
                </RadioButton>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    const { mode } = this.props;

    switch (mode) {
      case "view":
      case "preview":
        return this.renderView();
      case "edit":
      case "create":
        return this.renderEdit();
      case "evaluate":
        return this.renderEvaluationView();
      case "restricted-view":
        return this.renderView();
      default:
        return this.renderView();
    }
  }
}

export default RadioButtonPicker;
